import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';

export default class Filtering {
  constructor(el) {
    this.el = el;

    this.events = new EventHandler();

    this.filterInputs = null;
    this.dropdowns = this.el.querySelectorAll('[data-filter-group]');
    this.summaryEls = this.el.querySelectorAll('[data-filter-group-summary]');
    this.filterContents = this.el.querySelectorAll('[data-filter-group-content]');

    this.filterCheckboxes = this.el.querySelectorAll('.filter-icon--checkbox');

    this.closedState = 'closed';
    this.openState = 'open';
    this.disclosureAnimations = {};

    this.filterRange = this.el.querySelectorAll('[data-filter-range]');
    this.rangeNames = [];

    this.filterRange.forEach(filter => {
      this.rangeNames.push(filter.name);
    });

    this.updateTimeout = null;

    this._initFilterEvents = this._initFilterEvents.bind(this);
    this._updatePrice = this._updatePrice.bind(this);
    this._buildFilterQuery = this._buildFilterQuery.bind(this);

    this._initAnimations();
    this._initFilterEvents();
  }

  _initFilterEvents() {
    /* For radio buttons */
    this.filterInputs = this.el.querySelectorAll('[data-filter-input]');

    this.filterInputs.forEach(filter => {
      this.events.register(filter, 'click', e => {
        const target = e.currentTarget;
        if (target.hasAttribute('data-disabled')) {
          e.preventDefault();
          return;
        }

        this._filterClick(target);
      });
    });

    this.filterContents.forEach(content => {
      this.events.register(window, 'resize', () => this._setStyleVariables(content));
    });

    this.events.register(window, 'keyup', e => {
      if (e.key === 'Escape') {
        this._closeAllDropdowns();
      }
    });

    /* For range input */
    this.filterRange.forEach(filter => {
      this.events.register(filter, 'keyup', e => {
        if (e.key !== 'Tab') {
          this._updatePrice(e.currentTarget);
        }
      });

      this.events.register(filter, 'change', e => this._updatePrice(e.currentTarget));
    });

    this.summaryEls.forEach(summary => {
      this.events.register(summary, 'click', event => this._animateDisclosure(event));
    });

    /* When clicking outside dropdown element, close dropdown */
    this.events.register(document.body, 'click', e => {
      if (!e.target.closest('[data-filter-group]')) {
        this._closeAllDropdowns();
      }
    });
  }

  _filterClick(target) {
    if (target.dataset.hasOwnProperty('disabled')) return;

    const checkbox = target.querySelector('.filter-icon--checkbox .checkmark');
    const checkmark = target.querySelector('.filter-icon--checkbox .checkmark__check');

    if (target.hasAttribute('data-filter-active')) {
      checkbox.dataset.animationState = 'unchecked';
      checkmark.dataset.animationState = 'unchecked';
      target.removeAttribute('data-filter-active');
    } else {
      checkbox.dataset.animationState = 'checked';
      checkmark.dataset.animationState = 'checked';
      target.setAttribute('data-filter-active', '');
    }
  }

  /*
   * Initialize animations on checkbox container
   * using filter tag as JSON key
   */

  _initAnimations() {
    this.summaryEls.forEach(summary => {
      const filterList = summary.nextElementSibling;
      const currentDetailsEl = summary.closest('[data-filter-group]');
      const index = summary.dataset.filterGroupIndex;
      this.disclosureAnimations[index] = transition({ el: filterList, state: this.closedState });
      currentDetailsEl.style.setProperty('--closed-height', `${summary.offsetHeight}px`);
    });
  }

  _buildFilterQuery(element) {
    let searchParameters;

    // If input is price range, the url_to_add property is not available, so we
    // need to build the query manually
    if (element.dataset.hasOwnProperty('filterRange')) {
      const currentSearch = window.location.search.replace('?', '');
      const formData = new FormData(element.closest('form'));
      const newSearch = new URLSearchParams(formData).toString();

      // Filter through current search string to see if price range input already exists,
      // if it does, create new string without current price range parameters
      const splitCurrentSearch = currentSearch.split('&');

      let filteredCurrentSearch = splitCurrentSearch.filter(search => {
        if (search.includes(this.rangeNames[0]) || search.includes(this.rangeNames[1])) {
          return false;
        }

        return true;
      });

      filteredCurrentSearch = filteredCurrentSearch.join('&');

      // If current search parameters exist, append new search parameters
      // on to existing parameters
      if (filteredCurrentSearch.length > 0) {
        searchParameters = `${filteredCurrentSearch}&${newSearch}`;
      } else {
        searchParameters = `${newSearch}`;
      }
    }

    let urlQueryString = '';

    urlQueryString = `?${searchParameters}`;

    return urlQueryString;
  }

  _updatePrice(element) {
    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    // Wait for user to finish entering input before reloading page
    this.updateTimeout = setTimeout(() => {
      this._getFilteredResults(element);
    }, 2000);
  }

  _getFilteredResults(element) {
    const filterQuery = this._buildFilterQuery(element);
    window.location.search = filterQuery;
  }

  _animateDisclosure(event) {
    event.preventDefault();
    const summary = event.currentTarget;
    const filterList = summary.nextElementSibling;
    const currentDetailsEl = summary.closest('[data-filter-group]');

    /* Handle dropdowns on click event outside disclosure */
    if (filterList == null) {
      this._closeAllDropdowns();
    } else {
      if (filterList.dataset.animation) return;

      if (filterList.dataset.animationState === this.openState) {
        this._closeDisclosure(currentDetailsEl);
      } else {
        /* Close any open dropdowns before opening new dropdown */
        this._closeAllDropdowns();
        this._openDisclosure(currentDetailsEl);
      }
    }
  }

  _openDisclosure(detailsEl) {
    const index = detailsEl.querySelector('[data-filter-group-summary]').dataset.filterGroupIndex;
    detailsEl.setAttribute('open', '');

    this.disclosureAnimations[index]
      .animateTo(this.openState, {
        onStart: ({ el }) => {
          this._setStyleVariables(el);
        },
      });

    detailsEl.classList.add('dropdown-expanded');
  }

  _setStyleVariables(el) {
    const container = this.el.querySelector('[data-faceted-filter]');
    const containerStyle = window.getComputedStyle ? getComputedStyle(container, null) : container.currentStyle;
    const paddingLeft = parseInt(containerStyle.paddingLeft, 10) || 0;
    const paddingRight = parseInt(containerStyle.paddingRight, 10) || 0;

    const elementBounds = el.getBoundingClientRect();
    const rightBounds = elementBounds.right;
    const leftBounds = elementBounds.left;

    if (rightBounds >= window.innerWidth) {
      el.classList.add('filters--overflow-right');
    }

    if (leftBounds <= 0) {
      el.classList.add('filters--overflow-left');
    }

    const mobileWidth = container.getBoundingClientRect().width - paddingLeft - paddingRight;

    el.style.setProperty('--mobile-width', `${mobileWidth}px`);
    el.style.setProperty('--open-height', `${el.offsetHeight}px`);
  }

  _closeDisclosure(detailsEl) {
    const index = detailsEl.querySelector('[data-filter-group-summary]').dataset.filterGroupIndex;

    this.disclosureAnimations[index]
      .animateTo(this.closedState)
      .then(() => {
        detailsEl.removeAttribute('open');
      });

    detailsEl.classList.remove('dropdown-expanded');
  }

  _closeAllDropdowns() {
    this.dropdowns.forEach(dropdown => {
      if (dropdown.classList.contains('dropdown-expanded')) {
        this._closeDisclosure(dropdown);
      }
    });
  }

  unload() {
    if (this.events) {
      this.events.unregisterAll();
    }
  }
}
